import PropTypes from "prop-types";
import { useCallback } from "react";
import { useFieldArray } from "react-hook-form";
import { IconButton } from "@mui/material";
import { ScheduleFieldStyled } from "./schedule-field.style";
import { EncircledPlusIcon } from "../../../utils/svg-components";
import { TextInputFormField } from "../text-input-field/text-input-form-field";
import InfoIcon from "../../info-icon/info-icon";
import { MinusSvg } from "../../../utils/svg-components";
import { Typography } from "@mui/material";
import { v4 as uuidv4 } from "uuid";

const ScheduleField = ({
  control,
  isRequired = false,
  isAutoFocusEnabled,
  label,
  name,
  maxMonths = 12,
  disabled,
  onBlur,
  description,
  canAddFields = true,
  isTransparent,
}) => {
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: name,
    keyName: "id",
  });
  const onAddSchedule = useCallback(() => {
    append({ value: "", month: fields?.length + 1 });
  }, [append, fields?.length]);

  const onRemove = (index) => {
    if (fields.length === 1) return;
    remove(index);

    for (let i = index + 1; i < fields.length; i++) {
      const updatedField = {
        ...fields[i],
        month: fields[i].month - 1,
      };
      update(i - 1, updatedField);
    }
  };
  return (
    <ScheduleFieldStyled isTransparent={isTransparent}>
      {label && (
        <div className="label" data-testid="label">
          <Typography body1>
            {label}
            {isRequired && "*"}
          </Typography>
          {description && <InfoIcon message={description} />}
        </div>
      )}
      <div className="fields">
        {fields?.map((obj, index) => {
          return (
            <div className="width" key={obj.id}>
              <div
                className="minus"
                onClick={() => onRemove(index)}
                style={{ pointerEvents: disabled ? "none" : "all" }}
              >
                <MinusSvg />
              </div>
              <Typography variant="body3" className="month-label">
                {`Month ${index + 1}`}
              </Typography>
              <TextInputFormField
                name={`${name}.${index}.value`}
                control={control}
                className="input-field"
                placeholder="0.00"
                variant="outlined"
                inputType="text"
                disabled={disabled}
                onBlur={onBlur}
                autoFocus={isAutoFocusEnabled && fields?.length === index + 1}
                InputProps={{
                  startAdornment: <div>$</div>,
                }}
                sx={{
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                    {
                      display: "none",
                    },
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                  "& fieldset": { border: "none", padding: "0px !important" },
                }}
              />
            </div>
          );
        })}
        <div className="plus">
          <IconButton
            onClick={onAddSchedule}
            disabled={fields?.length === maxMonths || disabled}
            style={{
              border: "none",
              ...(fields?.length === maxMonths || disabled
                ? { opacity: 0.5 }
                : {}),
            }}
          >
            <EncircledPlusIcon />
          </IconButton>
        </div>
      </div>
    </ScheduleFieldStyled>
  );
};

ScheduleField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  maxMonths: PropTypes.number,
  isRequired: PropTypes.bool,
  control: PropTypes.any.isRequired,
  disabled: PropTypes.bool,
  onBlur: PropTypes.func,
};
export default ScheduleField;
